import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listProdutos } from '../api/produto';
import { formatMoney } from '../utils/formatters';
import { Plus, Package, ChevronRight, Search, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

export default function ProdutosListPage() {
  const { data, isLoading } = useQuery({ queryKey: ['produtos'], queryFn: listProdutos });
  const [busca, setBusca] = useState('');

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter((p) => {
      const cod = (p.codigo || '').toLowerCase();
      const desc = (p.descricao || '').toLowerCase();
      const ncm = (p.ncm || '').toLowerCase();
      const ean = (p.ean || '').toLowerCase();
      return cod.includes(q) || desc.includes(q) || ncm.includes(q) || ean.includes(q);
    });
  }, [data, busca]);

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="eyebrow">Cadastro</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Produtos</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">Catálogo fiscal de produtos e serviços para emissão.</p>
        </div>
        <Link to="/produtos/novo" className="btn-primary min-h-11 justify-center">
          <Plus className="h-4 w-4" />
          Novo
        </Link>
        </div>
      </header>

      <div className="relative rounded-2xl border border-slate-200 bg-white/90 p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
        <input
          type="search"
          className="field-input pl-9 pr-9"
          placeholder="Buscar por código, descrição, NCM ou EAN..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          autoComplete="off"
        />
        {busca && (
          <button
            type="button"
            onClick={() => setBusca('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-slate-950 rounded hover:bg-slate-100 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-white/5"
            aria-label="Limpar busca"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!isLoading && busca && (
          <p className="text-xs text-slate-500">
          {filtrados.length} resultado{filtrados.length === 1 ? '' : 's'} para &quot;{busca}&quot;
        </p>
      )}

      {isLoading && (
        <div className="py-10 grid place-items-center">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && filtrados.length === 0 && !busca && (
        <EmptyState
          title="Nenhum produto cadastrado"
          icon={<Package className="h-10 w-10" />}
          action={
            <Link to="/produtos/novo" className="btn-primary">
              <Plus className="h-4 w-4" />
              Cadastrar produto
            </Link>
          }
        />
      )}

      {!isLoading && filtrados.length === 0 && busca && (
        <div className="card-surface p-6 text-center text-sm text-slate-500">
          Nenhum produto encontrado para &quot;{busca}&quot;.
        </div>
      )}

      {!isLoading && filtrados.length > 0 && (
        <>
          <ul className="md:hidden space-y-2">
            {filtrados.map((p) => (
              <li key={p.id}>
                <Link to={`/produtos/${p.id}`} className="card-surface block p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.descricao || '(sem descrição)'}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {p.codigo ? `Cod ${p.codigo}` : ''} &middot; NCM {p.ncm || '-'} &middot; {formatMoney(p.valor_unitario as number)}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          <div className="hidden md:block card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 font-semibold">Cod</th>
                  <th className="px-4 py-3 font-semibold">Descrição</th>
                  <th className="px-4 py-3 font-semibold">NCM</th>
                  <th className="px-4 py-3 font-semibold">Unid. (UCom)</th>
                  <th className="px-4 py-3 font-semibold">Valor unit.</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {filtrados.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/70">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">{p.codigo || '-'}</td>
                    <td className="px-4 py-3">
                      <Link to={`/produtos/${p.id}`} className="font-medium text-primary">
                        {p.descricao || '(sem descrição)'}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">{p.ncm || '-'}</td>
                    <td className="px-4 py-3">{p.ucom || '-'}</td>
                    <td className="px-4 py-3 font-medium tabular-nums">{formatMoney(p.valor_unitario as number)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/produtos/${p.id}`} className="btn-secondary !px-3 !py-1.5 text-sm">
                        Editar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
